// batch-63 certificaat-mailbox (FO-469) — pure regels van de certificaat-mailbox.
//
// Geen I/O: de API laadt de medewerkers, types, synoniemen, opleiders en
// bestaande certificaten en vraagt hier om een BESLISSING. Zo zijn de regels die
// ertoe doen los te testen:
//
//   - medewerker: fuzzy maar alleen EENDUIDIG. Meerdere kandidaten -> geen keuze;
//     een naam die alleen op een oud-medewerker past wordt NOOIT aan een
//     actieve naamgenoot gekoppeld; een zwakke match heeft een bevestigende hint
//     nodig (geboortedatum, afzender, recente cursusdeelname).
//   - certificaattype: uitsluitend EXACT (naam, code, synoniem of een vast deel
//     van de typenaam), nooit fuzzy. "NEN 3140 VOP" en "NEN 3140 VP" of
//     "Hoogwerker 1A" en "Hoogwerker (algemeen)" zijn verschillende types en
//     mogen nooit samenvallen. Verzameltypes ("Overig ...") matchen nooit.
//   - dubbel: `vindMogelijkDubbelCertificaat` markeert, slaat nooit stil over.
//
// Datums zijn kalenderdagen ('yyyy-mm-dd'); in de DB staan ze als UTC-middernacht
// (dezelfde conventie als de certificaat-routes).

import type {
  CertificaatExtractieDTO,
  CertificaatExtractieVeld,
  CertificaatHerkomst,
  CertificaatReviewReden,
  CertificaatTypeMatchUitkomst,
  CertificaatTypeMatchVia,
  DubbelReden,
  ExtractieZekerheid,
  MedewerkerMatchHint,
  MedewerkerMatchKandidaatDTO,
  MedewerkerMatchNiveau,
  MedewerkerMatchUitkomst,
  OpleiderMatchVia,
  VervaldatumBron,
} from '@streep/types'
import { CERTIFICAAT_TYPE_OVERIG_CODE } from '@streep/types'
import { nlDagVanInstant } from './certificaat-status'
import { emailDomein, PUBLIEKE_MAILDOMEINEN } from './email-domein'

// ─── Dagen ───────────────────────────────────────────────────────────────────

const ISO_DAG_RE = /^(\d{4})-(\d{2})-(\d{2})$/
const NL_DAG_RE = /^(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})$/

function isKalenderDag(y: number, m: number, d: number): boolean {
  if (m < 1 || m > 12 || d < 1 || d > 31 || y < 1900 || y > 2200) return false
  const dt = new Date(Date.UTC(y, m - 1, d))
  return dt.getUTCFullYear() === y && dt.getUTCMonth() === m - 1 && dt.getUTCDate() === d
}

function pad2(n: number): string {
  return String(n).padStart(2, '0')
}

/**
 * Een kalender-geldige dag als 'yyyy-mm-dd', of null. Accepteert ISO
 * ('2026-02-28', ook met tijddeel) en de NL-notatie ('28-02-2026', '28/2/2026').
 * "2026-02-31" wordt null in plaats van stil door te rollen naar maart.
 */
export function schoonIsoDag(v: string | null | undefined): string | null {
  const t = (v ?? '').trim()
  if (!t) return null
  const iso = ISO_DAG_RE.exec(t.slice(0, 10))
  if (iso) {
    const [y, m, d] = [Number(iso[1]), Number(iso[2]), Number(iso[3])]
    return isKalenderDag(y, m, d) ? `${iso[1]}-${iso[2]}-${iso[3]}` : null
  }
  const nl = NL_DAG_RE.exec(t)
  if (nl) {
    const [d, m, y] = [Number(nl[1]), Number(nl[2]), Number(nl[3])]
    return isKalenderDag(y, m, d) ? `${y}-${pad2(m)}-${pad2(d)}` : null
  }
  return null
}

/** 'yyyy-mm-dd' -> UTC-middernacht (de dag-conventie van de certificaat-tabellen). */
export function isoDagNaarUtc(ymd: string): Date {
  return new Date(`${ymd.slice(0, 10)}T00:00:00.000Z`)
}

/**
 * Tel hele maanden op bij een kalenderdag; de dag wordt geklemd op het einde van
 * de doelmaand (31-01 + 1 maand = 28/29-02). Certificaten "5 jaar geldig" vallen
 * zo op dezelfde kalenderdag.
 */
export function telMaandenOpIsoDag(ymd: string, maanden: number): string {
  const m = ISO_DAG_RE.exec(ymd.slice(0, 10))
  if (!m) throw new Error(`Ongeldige dag: ${ymd}`)
  const jaar = Number(m[1])
  const maand0 = Number(m[2]) - 1 + Math.trunc(maanden)
  const doelJaar = jaar + Math.floor(maand0 / 12)
  const doelMaand0 = ((maand0 % 12) + 12) % 12
  const laatsteDag = new Date(Date.UTC(doelJaar, doelMaand0 + 1, 0)).getUTCDate()
  const dag = Math.min(Number(m[3]), laatsteDag)
  return `${doelJaar}-${pad2(doelMaand0 + 1)}-${pad2(dag)}`
}

function dagVan(v: Date | string | null | undefined): string | null {
  if (v === null || v === undefined) return null
  if (typeof v === 'string') {
    const schoon = schoonIsoDag(v)
    if (schoon) return schoon
  }
  const d = typeof v === 'string' ? new Date(v) : v
  return Number.isNaN(d.getTime()) ? null : nlDagVanInstant(d)
}

// ─── Namen normaliseren ──────────────────────────────────────────────────────

/** Nederlandse (en veelvoorkomende) tussenvoegsels; nooit onderdeel van de kern. */
const TUSSENVOEGSELS = new Set([
  'van', 'de', 'der', 'den', 'het', 'des', 'ter', 'ten', 'te', 'op', 'in', 'aan',
  'bij', 'uit', 'voor', 'onder', 'over', 'vd', 'la', 'le', 'du', 'da', 'di',
  'von', 'dos', 'del', 'della', 'el', 'al', 'lo', 'zu', 'zum', 'zur',
])

function zonderAccenten(s: string): string {
  return s.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
}

interface NaamToken {
  tekst: string
  /** Een losse letter (voorletter). */
  initiaal: boolean
}

/**
 * Splits een persoonsnaam in tokens: kleine letters, zonder accenten, met
 * voorletters als losse letters ("R.J." -> r, j) en "Achternaam, Voornaam"
 * omgedraaid naar de gewone volgorde.
 */
export function naamTokens(naam: string | null | undefined): NaamToken[] {
  let t = zonderAccenten((naam ?? '').toLowerCase())
    .replace(/[\u2018\u2019`]/g, "'")
    .replace(/(^|\s)'t(?=\s|$)/g, '$1het')
    .replace(/(^|\s)'s(?=\s|$)/g, '$1des')
    .trim()
  if (!t) return []
  // "Hendrikx, Rico" / "Berg, R.J. van den" -> "rico hendrikx" / "r.j. van den berg"
  const komma = t.indexOf(',')
  if (komma > 0) {
    const achter = t.slice(0, komma).trim()
    const voor = t.slice(komma + 1).trim()
    if (achter && voor) t = `${voor} ${achter}`
  }
  const uit: NaamToken[] = []
  for (const ruw of t.split(/\s+/)) {
    if (!ruw) continue
    // Voorletters met punten ("r.j." / "r.j") -> losse initialen.
    if (/^([a-z]\.)+[a-z]?\.?$/.test(ruw)) {
      for (const letter of ruw.replace(/\./g, '')) uit.push({ tekst: letter, initiaal: true })
      continue
    }
    for (const deel of ruw.split(/[^a-z0-9]+/)) {
      if (!deel) continue
      uit.push({ tekst: deel, initiaal: deel.length === 1 })
    }
  }
  return uit
}

/** Levenshtein-afstand (klein, alleen voor korte naamdelen). */
export function levenshtein(a: string, b: string): number {
  if (a === b) return 0
  if (a.length === 0) return b.length
  if (b.length === 0) return a.length
  let vorige = Array.from({ length: b.length + 1 }, (_, i) => i)
  for (let i = 1; i <= a.length; i++) {
    const huidige = [i]
    for (let j = 1; j <= b.length; j++) {
      const kosten = a[i - 1] === b[j - 1] ? 0 : 1
      huidige[j] = Math.min(huidige[j - 1]! + 1, vorige[j]! + 1, vorige[j - 1]! + kosten)
    }
    vorige = huidige
  }
  return vorige[b.length]!
}

/** Toegestane spelafstand voor een achternaam-kern (kort = exact). */
function achternaamTolerantie(lengte: number): number {
  if (lengte >= 10) return 2
  if (lengte >= 5) return 1
  return 0
}

// ─── Medewerker-match ────────────────────────────────────────────────────────

export interface MedewerkerVoorMatch {
  id: string
  firstName: string
  lastName: string
  employeeNumber: string | null
  /** UTC-dag-instant of 'yyyy-mm-dd'; null = onbekend. */
  birthDate: Date | string | null
  /** Lopend dienstverband (onboarding, in dienst, of gepland vertrek). */
  actief: boolean
  /** Werk-, privé- en accountadres; hoofdletters maken niet uit. */
  emailAdressen: string[]
  /** Certificaattypes waarvoor de medewerker recent een cursus volgde. */
  recenteDeelnameTypeIds?: string[]
  /** Opleiders bij wie de medewerker recent een cursus volgde. */
  recenteDeelnameOpleiderIds?: string[]
}

export interface MedewerkerMatchInvoer {
  deelnemerNaam: string | null
  /** 'yyyy-mm-dd' zoals gelezen, of null. */
  geboortedatum: string | null
  afzenderAdres: string | null
  /** Het (al gematchte) type: voor de deelname-hint. */
  certificateTypeId: string | null
  /** De (al gematchte) opleider: voor de deelname-hint. */
  opleiderId: string | null
}

export interface MedewerkerMatchResultaat {
  uitkomst: MedewerkerMatchUitkomst
  /** Alleen gevuld bij GEVONDEN. */
  employeeId: string | null
  /** Beste suggestie voor de review (alleen bij ONZEKER, nooit een oud-medewerker). */
  voorstelEmployeeId: string | null
  kandidaten: MedewerkerMatchKandidaatDTO[]
  toelichting: string
}

const NIVEAU_RANG: Record<MedewerkerMatchNiveau, number> = { EXACT: 3, STERK: 2, ZWAK: 1 }

function naamNiveau(kandidaat: NaamToken[], m: MedewerkerVoorMatch): MedewerkerMatchNiveau | null {
  const k = kandidaat.filter((t) => t.initiaal || !TUSSENVOEGSELS.has(t.tekst))
  const achter = naamTokens(m.lastName).filter((t) => !TUSSENVOEGSELS.has(t.tekst)).map((t) => t.tekst)
  const voor = naamTokens(m.firstName).map((t) => t.tekst)
  if (achter.length === 0 || k.length === 0) return null

  const kernKandidaat = achter.join('')
  let rest: NaamToken[] | null = null
  let spelvariant = false

  const eindigtOp =
    k.length >= achter.length &&
    achter.every((a, i) => k[k.length - achter.length + i]!.tekst === a)
  const begintMet =
    k.length > achter.length && achter.every((a, i) => k[i]!.tekst === a)
  if (eindigtOp) {
    rest = k.slice(0, k.length - achter.length)
  } else if (begintMet) {
    rest = k.slice(achter.length)
  } else if (k.length >= achter.length) {
    // Kleine spelvariant in de achternaam ("Hendriks" vs "Hendrikx").
    const staart = k.slice(k.length - achter.length)
    const tolerantie = achternaamTolerantie(kernKandidaat.length)
    if (
      tolerantie > 0 &&
      staart.every((t) => !t.initiaal) &&
      levenshtein(staart.map((t) => t.tekst).join(''), kernKandidaat) <= tolerantie
    ) {
      rest = k.slice(0, k.length - achter.length)
      spelvariant = true
    }
  }
  if (rest === null) return null

  let niveau: MedewerkerMatchNiveau
  if (rest.length === 0 || voor.length === 0) {
    niveau = 'ZWAK'
  } else {
    const eerste = rest[0]!
    const voornaam = voor[0]!
    if (!eerste.initiaal) {
      const volleNamen = rest.filter((t) => !t.initiaal).map((t) => t.tekst)
      if (
        volleNamen.length === rest.length &&
        volleNamen.length === voor.length &&
        volleNamen.every((n, i) => n === voor[i])
      ) {
        niveau = 'EXACT'
      } else if (eerste.tekst === voornaam || volleNamen.includes(voornaam)) {
        niveau = 'STERK'
      } else if (eerste.tekst[0] === voornaam[0] && levenshtein(eerste.tekst, voornaam) <= 1) {
        niveau = 'ZWAK'
      } else {
        return null
      }
    } else {
      const initialen = rest.map((t) => t.tekst[0]!)
      if (initialen[0] === voornaam[0]) {
        // Meerdere voorletters: waar de medewerker ook meerdere voornamen heeft,
        // moeten ze kloppen ("R.J." past niet op "Rico Pieter").
        const botst = initialen.some((l, i) => i < voor.length && voor[i]![0] !== l)
        if (botst) return null
        niveau = 'STERK'
      } else if (initialen.includes(voornaam[0]!)) {
        niveau = 'ZWAK'
      } else {
        return null
      }
    }
  }
  if (spelvariant) niveau = 'ZWAK'
  return niveau
}

function volledigeNaam(m: MedewerkerVoorMatch): string {
  return `${m.firstName} ${m.lastName}`.replace(/\s+/g, ' ').trim()
}

/**
 * Koppel een gelezen deelnemer aan een medewerker. Alleen een EENDUIDIGE
 * uitkomst levert `GEVONDEN` op; alles anders gaat naar de review-lijst.
 */
export function matchMedewerker(
  invoer: MedewerkerMatchInvoer,
  medewerkers: readonly MedewerkerVoorMatch[],
): MedewerkerMatchResultaat {
  const tokens = naamTokens(invoer.deelnemerNaam)
  const geboorte = schoonIsoDag(invoer.geboortedatum)
  const afzender = (invoer.afzenderAdres ?? '').trim().toLowerCase()

  if (tokens.length === 0) {
    return {
      uitkomst: 'ONBEKEND',
      employeeId: null,
      voorstelEmployeeId: null,
      kandidaten: [],
      toelichting: 'Er is geen deelnemersnaam gelezen.',
    }
  }

  interface Kandidaat {
    m: MedewerkerVoorMatch
    niveau: MedewerkerMatchNiveau
    hints: MedewerkerMatchHint[]
  }
  const kandidaten: Kandidaat[] = []
  let afgevallenOpGeboorte = 0
  for (const m of medewerkers) {
    const niveau = naamNiveau(tokens, m)
    if (!niveau) continue
    const eigenGeboorte = dagVan(m.birthDate)
    // Een bekende, afwijkende geboortedatum sluit de kandidaat uit.
    if (geboorte && eigenGeboorte && geboorte !== eigenGeboorte) {
      afgevallenOpGeboorte += 1
      continue
    }
    const hints: MedewerkerMatchHint[] = []
    if (geboorte && eigenGeboorte && geboorte === eigenGeboorte) hints.push('GEBOORTEDATUM')
    if (afzender && m.emailAdressen.some((a) => a.trim().toLowerCase() === afzender)) {
      hints.push('AFZENDER')
    }
    const deelnameType =
      invoer.certificateTypeId !== null &&
      (m.recenteDeelnameTypeIds ?? []).includes(invoer.certificateTypeId)
    const deelnameOpleider =
      invoer.opleiderId !== null &&
      (m.recenteDeelnameOpleiderIds ?? []).includes(invoer.opleiderId)
    if (deelnameType || deelnameOpleider) hints.push('DEELNAME')
    kandidaten.push({ m, niveau, hints })
  }

  kandidaten.sort(
    (a, b) =>
      NIVEAU_RANG[b.niveau] - NIVEAU_RANG[a.niveau] ||
      volledigeNaam(a.m).localeCompare(volledigeNaam(b.m), 'nl'),
  )
  const dto: MedewerkerMatchKandidaatDTO[] = kandidaten.map((k) => ({
    employeeId: k.m.id,
    naam: volledigeNaam(k.m),
    employeeNumber: k.m.employeeNumber,
    niveau: k.niveau,
    actief: k.m.actief,
    hints: k.hints,
  }))

  if (kandidaten.length === 0) {
    return {
      uitkomst: 'ONBEKEND',
      employeeId: null,
      voorstelEmployeeId: null,
      kandidaten: dto,
      toelichting: geboorte
        ? 'Geen medewerker met deze naam en geboortedatum.'
        : 'Geen medewerker met deze naam.',
    }
  }

  const topRang = NIVEAU_RANG[kandidaten[0]!.niveau]
  const top = kandidaten.filter((k) => NIVEAU_RANG[k.niveau] === topRang)
  const bevestigd = kandidaten.filter((k) => k.hints.includes('GEBOORTEDATUM'))

  let gekozen: Kandidaat | null = null
  let beslisreden = ''
  if (top.length === 1) {
    gekozen = top[0]!
    // Een lager gerangschikte kandidaat met een bevestigde geboortedatum
    // spreekt de topkandidaat tegen: dan kiezen we niet.
    if (bevestigd.length > 0 && !bevestigd.includes(gekozen)) {
      return {
        uitkomst: 'MEERDERE',
        employeeId: null,
        voorstelEmployeeId: null,
        kandidaten: dto,
        toelichting:
          'De naam en de geboortedatum wijzen naar verschillende medewerkers. Er is bewust niets gekozen.',
      }
    }
    beslisreden = 'enige medewerker met deze naam'
  } else {
    const topBevestigd = top.filter((k) => k.hints.includes('GEBOORTEDATUM'))
    const topAfzender = top.filter((k) => k.hints.includes('AFZENDER'))
    if (topBevestigd.length === 1) {
      gekozen = topBevestigd[0]!
      beslisreden = 'geboortedatum maakt de naam eenduidig'
    } else if (topAfzender.length === 1) {
      gekozen = topAfzender[0]!
      beslisreden = 'de medewerker mailde het certificaat zelf'
    } else {
      return {
        uitkomst: 'MEERDERE',
        employeeId: null,
        voorstelEmployeeId: null,
        kandidaten: dto,
        toelichting: `De naam past op ${top.length} medewerkers. Er is bewust niets gekozen.`,
      }
    }
  }

  if (!gekozen.m.actief) {
    // Nooit terugvallen op een (lagere) actieve naamgenoot.
    return {
      uitkomst: 'NIET_ACTIEF',
      employeeId: null,
      voorstelEmployeeId: null,
      kandidaten: dto,
      toelichting: `De naam past op ${volledigeNaam(gekozen.m)}, die niet (meer) in dienst is. Er wordt niet aan een naamgenoot gekoppeld.`,
    }
  }

  // Staat er een geboortedatum op het certificaat en is die bij de gekozen
  // medewerker niet te toetsen, dan is de keuze niet eenduidig: zeker niet als
  // een naamgenoot er juist op afviel, en anders alleen met een andere
  // bevestiging (de medewerker mailde zelf, of volgde recent de cursus).
  if (geboorte && !gekozen.hints.includes('GEBOORTEDATUM')) {
    const andereBevestiging = gekozen.hints.includes('AFZENDER') || gekozen.hints.includes('DEELNAME')
    if (afgevallenOpGeboorte > 0 || !andereBevestiging) {
      return {
        uitkomst: 'ONZEKER',
        employeeId: null,
        voorstelEmployeeId: gekozen.m.id,
        kandidaten: dto,
        toelichting:
          afgevallenOpGeboorte > 0
            ? `Een naamgenoot viel af op de geboortedatum; van ${volledigeNaam(gekozen.m)} is geen geboortedatum bekend om te vergelijken. Controleer de medewerker.`
            : `Op het certificaat staat een geboortedatum, maar van ${volledigeNaam(gekozen.m)} is die niet bekend om te vergelijken. Controleer de medewerker.`,
      }
    }
  }

  if (gekozen.niveau === 'ZWAK' && gekozen.hints.length === 0) {
    return {
      uitkomst: 'ONZEKER',
      employeeId: null,
      voorstelEmployeeId: gekozen.m.id,
      kandidaten: dto,
      toelichting: `Alleen een zwakke naam-match met ${volledigeNaam(gekozen.m)}, zonder bevestiging door geboortedatum, afzender of cursusdeelname.`,
    }
  }

  const hintTekst = gekozen.hints.length > 0 ? ` (${gekozen.hints.map((h) => HINT_TEKST[h]).join(', ')})` : ''
  return {
    uitkomst: 'GEVONDEN',
    employeeId: gekozen.m.id,
    voorstelEmployeeId: gekozen.m.id,
    kandidaten: dto,
    toelichting: `${volledigeNaam(gekozen.m)}: ${beslisreden}${hintTekst}.`,
  }
}

const HINT_TEKST: Record<MedewerkerMatchHint, string> = {
  AFZENDER: 'mailde zelf',
  DEELNAME: 'volgde recent deze cursus',
  GEBOORTEDATUM: 'geboortedatum klopt',
}

// ─── Certificaattype-match ───────────────────────────────────────────────────

export interface CertificaatTypeVoorMatch {
  id: string
  name: string
  code: string
  isActive: boolean
  validityMonths: number | null
}

export interface CertificaatSynoniemVoorMatch {
  certificateTypeId: string
  sleutel: string
}

/**
 * De matchsleutel van een certificaat-/cursusnaam: kleine letters, zonder
 * accenten en leestekens, tokens alfabetisch. "VCA-B" en "B-VCA" worden dus
 * dezelfde sleutel ("b vca"), "VCA Basis" wordt "basis vca". Geen stamwoorden,
 * geen fuzzy: twee namen matchen alleen als hun woorden exact gelijk zijn.
 */
export function normaliseerCertificaatNaam(naam: string | null | undefined): string {
  const tokens = zonderAccenten((naam ?? '').toLowerCase())
    .split(/[^a-z0-9]+/)
    .filter((t) => t.length > 0)
  return [...new Set(tokens)].sort().join(' ')
}

/**
 * Verzameltypes ("Overig (geïmporteerd)", het portal-vangnet) mogen nooit
 * automatisch gekozen worden: ze zeggen niets over wat het certificaat is.
 */
export function isVerzamelCertificaatType(t: { name: string; code: string }): boolean {
  if (t.code === CERTIFICAAT_TYPE_OVERIG_CODE) return true
  const eerste = normaliseerCertificaatNaam(t.name.split(/[\s(]/)[0] ?? '')
  return eerste === 'overig' || eerste === 'overige'
}

/**
 * batch-63 integratie (review B1) - is een gelezen naam te algemeen om als
 * synoniem van één type te onthouden? Ja als ALLE woorden ervan ook in de naam of
 * code van een ander actief type staan: "NEN 3140" past bij VOP én VP,
 * "Hoogwerker" bij 1A én 1B, "Lassen" bij TIG, MIG/MAG en MMA. Zo'n synoniem zou
 * de ene variant stil als de andere laten herkennen (screening!). Verzameltypes
 * en inactieve types tellen niet mee.
 */
export function certificaatSynoniemTeAlgemeen(
  naam: string | null | undefined,
  doelTypeId: string,
  types: readonly { id: string; name: string; code: string | null; isActive?: boolean }[],
): { teAlgemeen: boolean; ookVan: string[] } {
  const woorden = normaliseerCertificaatNaam(naam)
    .split(' ')
    .filter((w) => w.length > 0)
  if (woorden.length === 0) return { teAlgemeen: false, ookVan: [] }
  const ookVan = types
    .filter(
      (t) =>
        t.id !== doelTypeId &&
        t.isActive !== false &&
        !isVerzamelCertificaatType({ name: t.name, code: t.code ?? '' }),
    )
    .filter((t) => {
      const eigen = new Set(
        `${normaliseerCertificaatNaam(t.name)} ${normaliseerCertificaatNaam(t.code)}`
          .split(' ')
          .filter((w) => w.length > 0),
      )
      return woorden.every((w) => eigen.has(w))
    })
    .map((t) => t.name)
    .sort((a, b) => a.localeCompare(b, 'nl'))
  return { teAlgemeen: ookVan.length > 0, ookVan }
}

/** Vaste delen van een typenaam rond " - " of " / " ("NEN 3140 VP - Vakbekwaam persoon"). */
/** Woorden die op zichzelf geen certificaat aanduiden ("Heftruck - Basis"). */
const GENERIEKE_NAAMWOORDEN = new Set([
  'basis', 'vol', 'algemeen', 'gevorderd', 'gevorderden', 'beginner', 'beginners', 'herhaling',
  'herhalingscursus', 'opfris', 'opfriscursus', 'update', 'theorie', 'praktijk', 'examen', 'cursus',
  'training', 'opleiding', 'certificaat', 'diploma', 'niveau', 'module', 'deel', 'dag', 'intern', 'extern',
])

/**
 * Een vast deel van een typenaam telt alleen als het zelf specifiek is: minstens
 * twee woorden of een cijfer, en niet alleen generieke woorden. "NEN 3140 VOP"
 * en "Vakbekwaam persoon" wel; "Basis" of "Heftruck" niet.
 */
function isSpecifiekNaamDeel(deel: string): boolean {
  const woorden = deel.toLowerCase().split(/[^\p{L}\p{N}]+/u).filter((w) => w.length > 0)
  if (woorden.length === 0 || woorden.every((w) => GENERIEKE_NAAMWOORDEN.has(w))) return false
  return woorden.length >= 2 || /\d/.test(deel)
}

function naamDelen(naam: string): string[] {
  const delen = naam.split(/\s+[-/]\s+/).map((d) => d.trim()).filter((d) => d.length > 0)
  return delen.length > 1 ? delen.filter(isSpecifiekNaamDeel) : []
}

export interface CertificaatTypeMatchResultaat {
  uitkomst: CertificaatTypeMatchUitkomst
  certificateTypeId: string | null
  via: CertificaatTypeMatchVia | null
  kandidaatIds: string[]
  toelichting: string
}

export function matchCertificaatType(
  naam: string | null | undefined,
  types: readonly CertificaatTypeVoorMatch[],
  synoniemen: readonly CertificaatSynoniemVoorMatch[],
): CertificaatTypeMatchResultaat {
  const sleutel = normaliseerCertificaatNaam(naam)
  if (!sleutel) {
    return {
      uitkomst: 'ONBEKEND',
      certificateTypeId: null,
      via: null,
      kandidaatIds: [],
      toelichting: 'Er is geen cursus- of certificaatnaam gelezen.',
    }
  }
  const bruikbaar = types.filter((t) => t.isActive && !isVerzamelCertificaatType(t))
  const bruikbaarIds = new Set(bruikbaar.map((t) => t.id))

  const exact = new Map<string, CertificaatTypeMatchVia>()
  const voegToe = (id: string, via: CertificaatTypeMatchVia): void => {
    if (!exact.has(id)) exact.set(id, via)
  }
  for (const t of bruikbaar) {
    if (normaliseerCertificaatNaam(t.name) === sleutel) voegToe(t.id, 'NAAM')
  }
  for (const t of bruikbaar) {
    if (normaliseerCertificaatNaam(t.code) === sleutel) voegToe(t.id, 'CODE')
  }
  for (const s of synoniemen) {
    if (s.sleutel === sleutel && bruikbaarIds.has(s.certificateTypeId)) {
      voegToe(s.certificateTypeId, 'SYNONIEM')
    }
  }

  const naamVan = (id: string): string => bruikbaar.find((t) => t.id === id)?.name ?? id
  if (exact.size === 1) {
    const [[id, via]] = [...exact.entries()] as [[string, CertificaatTypeMatchVia]]
    return {
      uitkomst: 'GEVONDEN',
      certificateTypeId: id,
      via,
      kandidaatIds: [id],
      toelichting: `Herkend als ${naamVan(id)} (via ${VIA_TEKST[via]}).`,
    }
  }
  if (exact.size > 1) {
    const ids = [...exact.keys()]
    return {
      uitkomst: 'MEERDERE',
      certificateTypeId: null,
      via: null,
      kandidaatIds: ids,
      toelichting: `De naam past op ${ids.length} types (${ids.map(naamVan).join(', ')}). Kies het juiste type.`,
    }
  }

  const deelIds = new Set<string>()
  for (const t of bruikbaar) {
    if (naamDelen(t.name).some((d) => normaliseerCertificaatNaam(d) === sleutel)) deelIds.add(t.id)
  }
  if (deelIds.size === 1) {
    const [id] = [...deelIds] as [string]
    return {
      uitkomst: 'GEVONDEN',
      certificateTypeId: id,
      via: 'NAAMDEEL',
      kandidaatIds: [id],
      toelichting: `Herkend als ${naamVan(id)} (via ${VIA_TEKST.NAAMDEEL}).`,
    }
  }
  if (deelIds.size > 1) {
    const ids = [...deelIds]
    return {
      uitkomst: 'MEERDERE',
      certificateTypeId: null,
      via: null,
      kandidaatIds: ids,
      toelichting: `De naam past op ${ids.length} types (${ids.map(naamVan).join(', ')}). Kies het juiste type.`,
    }
  }
  return {
    uitkomst: 'ONBEKEND',
    certificateTypeId: null,
    via: null,
    kandidaatIds: [],
    toelichting: `"${(naam ?? '').trim()}" is geen bekend certificaattype of synoniem.`,
  }
}

const VIA_TEKST: Record<CertificaatTypeMatchVia, string> = {
  NAAM: 'de naam van het type',
  NAAMDEEL: 'een deel van de typenaam',
  CODE: 'de code van het type',
  SYNONIEM: 'een synoniem',
}

/** De AI-typesuggestie terugvinden (exacte naam). Alleen een hint in de review. */
export function vindTypeSuggestie(
  suggestie: string | null | undefined,
  types: readonly CertificaatTypeVoorMatch[],
): string | null {
  const sleutel = normaliseerCertificaatNaam(suggestie)
  if (!sleutel) return null
  const hits = types.filter(
    (t) => t.isActive && !isVerzamelCertificaatType(t) && normaliseerCertificaatNaam(t.name) === sleutel,
  )
  return hits.length === 1 ? hits[0]!.id : null
}

// ─── Opleider-match ──────────────────────────────────────────────────────────

export interface OpleiderVoorMatch {
  id: string
  naam: string
  contactEmail: string | null
  website: string | null
  isActive: boolean
}

const RECHTSVORM_WOORDEN = new Set(['bv', 'b', 'v', 'nv', 'vof', 'cv', 'holding', 'groep', 'group', 'the'])

function normaliseerOrganisatie(naam: string | null | undefined): string {
  return zonderAccenten((naam ?? '').toLowerCase())
    .split(/[^a-z0-9]+/)
    .filter((t) => t.length > 0 && !RECHTSVORM_WOORDEN.has(t))
    .join(' ')
}

function websiteDomein(url: string | null | undefined): string | null {
  const t = (url ?? '').trim().toLowerCase()
  if (!t) return null
  const zonderSchema = t.replace(/^[a-z]+:\/\//, '')
  const host = zonderSchema.split(/[/?#]/)[0]?.replace(/^www\./, '') ?? ''
  return host.includes('.') ? host : null
}

export interface OpleiderMatchResultaat {
  opleiderId: string | null
  naam: string | null
  via: OpleiderMatchVia | null
}

/**
 * Opleider via de naam op het certificaat, anders via het afzenderdomein. Alleen
 * een eenduidige treffer telt; onbekend blijft vrije tekst in `uitgever`.
 */
export function matchOpleider(
  invoer: { namen: Array<string | null | undefined>; afzenderAdres: string | null },
  opleiders: readonly OpleiderVoorMatch[],
  opts: { interneDomeinen?: readonly string[] } = {},
): OpleiderMatchResultaat {
  const actief = opleiders.filter((o) => o.isActive)
  for (const naam of invoer.namen) {
    const sleutel = normaliseerOrganisatie(naam)
    if (!sleutel) continue
    const hits = actief.filter((o) => normaliseerOrganisatie(o.naam) === sleutel)
    if (hits.length === 1) return { opleiderId: hits[0]!.id, naam: hits[0]!.naam, via: 'NAAM' }
  }
  const domein = emailDomein(invoer.afzenderAdres)
  const intern = new Set((opts.interneDomeinen ?? []).map((d) => d.toLowerCase()))
  if (domein && !PUBLIEKE_MAILDOMEINEN.has(domein) && !intern.has(domein)) {
    const hits = actief.filter(
      (o) => emailDomein(o.contactEmail) === domein || websiteDomein(o.website) === domein,
    )
    if (hits.length === 1) {
      return { opleiderId: hits[0]!.id, naam: hits[0]!.naam, via: 'AFZENDERDOMEIN' }
    }
  }
  return { opleiderId: null, naam: null, via: null }
}

// ─── Datums ──────────────────────────────────────────────────────────────────

export interface CertificaatDatumBepaling {
  issuedAt: string | null
  geldigVanaf: string | null
  expiresAt: string | null
  expiresAfgeleid: boolean
  vervaldatumBron: VervaldatumBron
  redenen: CertificaatReviewReden[]
  toelichting: string
}

function isLaag(z: ExtractieZekerheid | null | undefined): boolean {
  return z === 'LAAG'
}

/**
 * Uitgifte-, ingangs- en vervaldatum. Volgorde voor de vervaldatum: de datum op
 * het certificaat, anders de geldigheidsduur op het certificaat, anders de
 * standaard geldigheidsduur van het type (beide "afgeleid"). Geen van drieën bij
 * een bekend type -> GEEN_GELDIGHEID (review).
 */
export function bepaalCertificaatDatums(
  extractie: Pick<
    CertificaatExtractieDTO,
    'uitgifteDatum' | 'geldigVanaf' | 'vervalDatum' | 'geldigheidsduurMaanden'
  >,
  type: { validityMonths: number | null } | null,
): CertificaatDatumBepaling {
  const redenen: CertificaatReviewReden[] = []
  const issuedAt = schoonIsoDag(extractie.uitgifteDatum.waarde)
  const geldigVanaf = schoonIsoDag(extractie.geldigVanaf.waarde)
  const vervalDocument = schoonIsoDag(extractie.vervalDatum.waarde)
  const duurDocument = geldigeMaanden(extractie.geldigheidsduurMaanden.waarde)
  const basis = geldigVanaf ?? issuedAt

  if (!issuedAt) redenen.push('DATUM_ONTBREEKT')
  if (issuedAt && isLaag(extractie.uitgifteDatum.zekerheid)) redenen.push('DATUM_ONZEKER')

  let expiresAt: string | null = null
  let bron: VervaldatumBron = 'GEEN'
  let toelichting: string
  if (vervalDocument) {
    expiresAt = vervalDocument
    bron = 'DOCUMENT'
    toelichting = 'Vervaldatum staat op het certificaat.'
    if (isLaag(extractie.vervalDatum.zekerheid)) redenen.push('DATUM_ONZEKER')
  } else if (duurDocument !== null && basis) {
    expiresAt = telMaandenOpIsoDag(basis, duurDocument)
    bron = 'DUUR_DOCUMENT'
    toelichting = `Vervaldatum berekend: ${duurDocument} maanden geldig volgens het certificaat.`
    if (isLaag(extractie.geldigheidsduurMaanden.zekerheid)) redenen.push('DATUM_ONZEKER')
  } else if (type && type.validityMonths && type.validityMonths > 0 && basis) {
    expiresAt = telMaandenOpIsoDag(basis, type.validityMonths)
    bron = 'DUUR_TYPE'
    toelichting = `Vervaldatum berekend uit de standaard geldigheidsduur van het type (${type.validityMonths} maanden).`
  } else {
    toelichting = 'Geen vervaldatum en geen geldigheidsduur bekend.'
    if (type && issuedAt) redenen.push('GEEN_GELDIGHEID')
  }

  if (expiresAt && issuedAt && expiresAt < issuedAt && !redenen.includes('DATUM_ONZEKER')) {
    redenen.push('DATUM_ONZEKER')
    toelichting = `${toelichting} De vervaldatum ligt vóór de uitgiftedatum.`
  }

  return {
    issuedAt,
    geldigVanaf,
    expiresAt,
    expiresAfgeleid: bron === 'DUUR_DOCUMENT' || bron === 'DUUR_TYPE',
    vervaldatumBron: bron,
    redenen: [...new Set(redenen)],
    toelichting,
  }
}

function geldigeMaanden(v: number | null): number | null {
  if (v === null || !Number.isFinite(v)) return null
  const n = Math.round(v)
  return n >= 1 && n <= 600 ? n : null
}

// ─── Dubbel-detectie ─────────────────────────────────────────────────────────

export interface BestaandCertificaatVoorDubbel {
  soort: 'CERTIFICAAT' | 'AANVRAAG'
  id: string
  certificateTypeId: string
  certificaatNummer: string | null
  issuedAt: Date | string | null
  expiresAt: Date | string | null
  /** Bekende sha256-hashes van de bijbehorende bestanden. */
  bestandHashes: readonly string[]
  /** Certificaat: lifecycle (ACTIEF/VERVANGEN/...); aanvraag: status. */
  status: string
}

export interface DubbelKandidaat {
  certificateTypeId: string | null
  certificaatNummer: string | null
  /** 'yyyy-mm-dd' of instant. */
  issuedAt: Date | string | null
  expiresAt: Date | string | null
  bestandHash: string | null
}

export interface MogelijkDubbel {
  soort: 'CERTIFICAAT' | 'AANVRAAG'
  id: string
  redenen: DubbelReden[]
}

function normaliseerNummer(n: string | null | undefined): string {
  return (n ?? '').toUpperCase().replace(/[^A-Z0-9]/g, '')
}

/**
 * Lijkt de kandidaat op iets wat er al is? Nooit stil overslaan: de uitkomst is
 * een markering met de bestaande erbij; HR kiest koppelen, toch indienen of
 * negeren.
 *
 *   - BESTAND: zelfde bestandsinhoud (ook bij een ander type, ook bij een eerder
 *     afgewezen aanvraag - dan is het hetzelfde papier dat terugkomt);
 *   - zelfde type +
 *       NUMMER            zelfde certificaatnummer,
 *       UITGIFTEDATUM     zelfde uitgiftedag,
 *       VERVALDATUM       zelfde vervaldag,
 *       OUDER_DAN_BESTAAND ouder dan een lopende versie (goedkeuren zou een
 *                         nieuwer certificaat uit de keten duwen),
 *       OPEN_AANVRAAG     er staat al een aanvraag open voor dit type.
 *
 * Een vroege vernieuwing (nieuwere uitgiftedatum binnen de looptijd van het oude
 * certificaat) is uitdrukkelijk GEEN dubbel.
 */
export function vindMogelijkDubbelCertificaat(
  bestaande: readonly BestaandCertificaatVoorDubbel[],
  kandidaat: DubbelKandidaat,
): MogelijkDubbel[] {
  const uit: MogelijkDubbel[] = []
  const nummer = normaliseerNummer(kandidaat.certificaatNummer)
  const uitgifte = dagVan(kandidaat.issuedAt)
  const verval = dagVan(kandidaat.expiresAt)

  for (const b of bestaande) {
    const vervallenAanvraag = b.soort === 'AANVRAAG' && b.status === 'INGETROKKEN'
    const geannuleerd = b.soort === 'CERTIFICAAT' && b.status === 'GEANNULEERD'
    if (vervallenAanvraag || geannuleerd) continue
    const afgewezen =
      (b.soort === 'AANVRAAG' && b.status === 'AFGEKEURD') ||
      (b.soort === 'CERTIFICAAT' && b.status === 'AFGEWEZEN')

    const redenen: DubbelReden[] = []
    if (kandidaat.bestandHash && b.bestandHashes.includes(kandidaat.bestandHash)) {
      redenen.push('BESTAND')
    }
    const zelfdeType =
      kandidaat.certificateTypeId !== null && kandidaat.certificateTypeId === b.certificateTypeId
    if (zelfdeType && !afgewezen) {
      if (nummer && nummer === normaliseerNummer(b.certificaatNummer)) redenen.push('NUMMER')
      const bUitgifte = dagVan(b.issuedAt)
      const bVerval = dagVan(b.expiresAt)
      if (uitgifte && bUitgifte && uitgifte === bUitgifte) redenen.push('UITGIFTEDATUM')
      if (verval && bVerval && verval === bVerval) redenen.push('VERVALDATUM')
      if (
        b.soort === 'CERTIFICAAT' &&
        b.status !== 'VERVANGEN' &&
        uitgifte &&
        bUitgifte &&
        uitgifte < bUitgifte
      ) {
        redenen.push('OUDER_DAN_BESTAAND')
      }
      if (b.soort === 'AANVRAAG' && b.status === 'INGEDIEND') redenen.push('OPEN_AANVRAAG')
    }
    if (redenen.length > 0) uit.push({ soort: b.soort, id: b.id, redenen })
  }
  return uit
}

// ─── Beslissing: automatisch voorstel of review ──────────────────────────────

export interface CertificaatVoorstelBeslisInvoer {
  medewerker: MedewerkerMatchUitkomst
  type: CertificaatTypeMatchUitkomst
  datumRedenen: readonly CertificaatReviewReden[]
  deelnemerZekerheid: ExtractieZekerheid | null
  dubbel: readonly MogelijkDubbel[]
  bundelNietSplitsbaar: boolean
}

const MEDEWERKER_REDEN: Record<Exclude<MedewerkerMatchUitkomst, 'GEVONDEN'>, CertificaatReviewReden> = {
  ONBEKEND: 'MEDEWERKER_ONBEKEND',
  MEERDERE: 'MEDEWERKER_MEERDERE',
  NIET_ACTIEF: 'MEDEWERKER_NIET_ACTIEF',
  ONZEKER: 'MEDEWERKER_ONZEKER',
}

/**
 * Alleen als ALLES eenduidig is ontstaat automatisch een aanvraag (die HR nog
 * steeds goedkeurt). Elke twijfel wordt een zichtbare review-reden.
 */
export function beslisCertificaatVoorstel(invoer: CertificaatVoorstelBeslisInvoer): {
  automatisch: boolean
  redenen: CertificaatReviewReden[]
} {
  const redenen: CertificaatReviewReden[] = []
  if (invoer.medewerker !== 'GEVONDEN') {
    redenen.push(MEDEWERKER_REDEN[invoer.medewerker])
  } else if (invoer.deelnemerZekerheid === 'LAAG') {
    redenen.push('MEDEWERKER_ONZEKER')
  }
  if (invoer.type === 'ONBEKEND') redenen.push('TYPE_ONBEKEND')
  if (invoer.type === 'MEERDERE') redenen.push('TYPE_MEERDERE')
  redenen.push(...invoer.datumRedenen)
  if (invoer.dubbel.length > 0) redenen.push('MOGELIJK_DUBBEL')
  if (invoer.bundelNietSplitsbaar) redenen.push('BUNDEL_NIET_SPLITSBAAR')
  const uniek = [...new Set(redenen)]
  return { automatisch: uniek.length === 0, redenen: uniek }
}

// ─── Bundels ─────────────────────────────────────────────────────────────────

export type BundelSplitsModus = 'HEEL' | 'SPLITSEN' | 'NIET_SPLITSBAAR'

/**
 * Mag een document per deelnemer gesplitst worden? Eén certificaat: het hele
 * document hoort erbij. Meerdere: alleen splitsen als elk certificaat eigen,
 * geldige pagina's heeft - een pagina met meerdere deelnemers (deelnemerslijst)
 * of een afbeelding met meerdere deelnemers is niet te splitsen, en dan gaat het
 * bestand nooit automatisch naar één medewerker (die zou dan collega's zien).
 */
export function beoordeelBundelSplitsing(
  certificaten: ReadonlyArray<{ paginas: readonly number[] }>,
  paginaAantal: number | null,
): BundelSplitsModus {
  if (certificaten.length <= 1) return 'HEEL'
  if (!paginaAantal || paginaAantal < 1) return 'NIET_SPLITSBAAR'
  const gezien = new Set<number>()
  for (const c of certificaten) {
    if (c.paginas.length === 0) return 'NIET_SPLITSBAAR'
    for (const p of c.paginas) {
      if (!Number.isInteger(p) || p < 1 || p > paginaAantal) return 'NIET_SPLITSBAAR'
      if (gezien.has(p)) return 'NIET_SPLITSBAAR'
    }
    for (const p of c.paginas) gezien.add(p)
  }
  return 'SPLITSEN'
}

// ─── Links in de mailtekst ───────────────────────────────────────────────────

const LINK_RE = /\bhttps?:\/\/[^\s<>"')\]]+/gi
const LINK_TREFWOORDEN = [
  'certific', 'certificaat', 'diploma', 'cert', 'download', 'examen', 'exam',
  '.pdf', 'vca', 'bewijs', 'deelname', 'pasje', 'opleidingsportal', 'mijn-opleiding',
  'getuigschrift', 'attest',
]
const LINK_UITSLUITINGEN = [
  'unsubscribe', 'afmelden', 'uitschrijven', 'privacy', 'disclaimer', 'linkedin.com',
  'facebook.com', 'twitter.com', 'x.com/', 'instagram.com', 'youtube.com', 'google.com/maps',
  'aka.ms', 'teams.microsoft.com',
]

/**
 * Outlook-safelinks uitpakken naar de echte URL. Alleen `http:`/`https:` komt
 * terug: de uitgepakte `?url=` is afzender-invoer en kan `javascript:` of
 * `data:` zijn, en de link wordt in de backoffice klikbaar getoond.
 */
function veiligeLink(url: string): string | null {
  let u: URL
  try {
    u = new URL(url)
  } catch {
    return null
  }
  if (u.hostname.endsWith('safelinks.protection.outlook.com')) {
    const echt = u.searchParams.get('url')
    if (echt) return veiligeLink(echt)
  }
  return u.protocol === 'http:' || u.protocol === 'https:' ? u.toString() : null
}

/** Is dit een link die de backoffice veilig klikbaar mag tonen? */
export function isVeiligeWebLink(url: string | null | undefined): boolean {
  if (!url) return false
  try {
    const u = new URL(url)
    return u.protocol === 'http:' || u.protocol === 'https:'
  } catch {
    return false
  }
}

/**
 * Links in de mailtekst die naar een certificaat lijken te wijzen (opleider-
 * portaal, downloadlink). Handtekening-, social- en afmeldlinks tellen niet; een
 * link naar het domein van een bekende opleider wel.
 */
export function vindCertificaatLinks(
  tekst: string | null | undefined,
  opts: { opleiderDomeinen?: readonly string[]; eigenDomeinen?: readonly string[] } = {},
): string[] {
  const gevonden = new Set<string>()
  const opleider = new Set((opts.opleiderDomeinen ?? []).map((d) => d.toLowerCase()))
  const eigen = (opts.eigenDomeinen ?? []).map((d) => d.toLowerCase())
  for (const ruw of (tekst ?? '').match(LINK_RE) ?? []) {
    const url = veiligeLink(ruw.replace(/[.,;:!?]+$/, ''))
    if (url === null) continue
    const laag = url.toLowerCase()
    if (LINK_UITSLUITINGEN.some((u) => laag.includes(u))) continue
    const host = websiteDomein(url)
    if (host && eigen.some((d) => host === d || host.endsWith(`.${d}`))) continue
    const trefwoord = LINK_TREFWOORDEN.some((w) => laag.includes(w))
    const bijOpleider = host !== null && opleider.has(host)
    if (trefwoord || bijOpleider) gevonden.add(url)
    if (gevonden.size >= 10) break
  }
  return [...gevonden]
}

// ─── Afzender ────────────────────────────────────────────────────────────────

const AUTOMATISCHE_LOKALE_DELEN = /^(no-?reply|do-?not-?reply|mailer-daemon|postmaster|bounces?|notifications?|noreply-[a-z0-9-]+)$/

/** Adressen waar nooit een terugkoppeling naartoe gaat (lussen, bounces). */
export function isAutomatischAfzenderAdres(adres: string | null | undefined): boolean {
  const t = (adres ?? '').trim().toLowerCase()
  const at = t.indexOf('@')
  if (at < 1) return true
  return AUTOMATISCHE_LOKALE_DELEN.test(t.slice(0, at))
}

/**
 * Intern = het adres hoort bij een bekende medewerker/gebruiker, of het domein
 * staat in de lijst interne domeinen (instelling, default streep.nl).
 */
export function isInterneAfzender(
  adres: string | null | undefined,
  opts: { interneDomeinen: readonly string[]; bekendeAdressen: ReadonlySet<string> },
): boolean {
  const t = (adres ?? '').trim().toLowerCase()
  if (!t) return false
  if (opts.bekendeAdressen.has(t)) return true
  const domein = emailDomein(t)
  if (!domein) return false
  return opts.interneDomeinen.some((d) => {
    const dd = d.trim().toLowerCase()
    return dd.length > 0 && (domein === dd || domein.endsWith(`.${dd}`))
  })
}

/**
 * Mag de afzender een terugkoppeling krijgen? Default aan voor interne
 * afzenders, uit voor externe (opleiders). Nooit naar de mailbox zelf of naar
 * een automatisch adres.
 */
export function magAfzenderTerugkoppelen(invoer: {
  afzenderAdres: string | null | undefined
  mailboxAdres: string | null | undefined
  intern: boolean
  terugkoppelingIntern: boolean
  terugkoppelingExtern: boolean
}): boolean {
  const adres = (invoer.afzenderAdres ?? '').trim().toLowerCase()
  if (!adres || isAutomatischAfzenderAdres(adres)) return false
  if (adres === (invoer.mailboxAdres ?? '').trim().toLowerCase()) return false
  return invoer.intern ? invoer.terugkoppelingIntern : invoer.terugkoppelingExtern
}

// ─── Herkomst van een certificaatversie (§7/§15) ──────────────────────────────

/**
 * Waar een certificaatversie vandaan kwam, afgeleid uit wat er al ligt: de
 * aanvraag die hem opleverde wint (ook een door HR vanuit de mailbox ingevulde
 * aanvraag telt als mailbox), anders de bron in de aanmaak-audit. Zonder
 * herkenbare bron (oude rijen, imports, HR-invoer) is het handmatig.
 */
export function bepaalCertificaatHerkomst(invoer: {
  aanvraagBron: string | null | undefined
  auditBron: string | null | undefined
}): CertificaatHerkomst {
  if (invoer.aanvraagBron === 'MAILBOX' || invoer.aanvraagBron === 'HANDMATIG') return 'MAILBOX'
  if (invoer.aanvraagBron === 'PORTAL') return 'PORTAL'
  switch (invoer.auditBron) {
    case 'CERTIFICAAT_MAILBOX':
      return 'MAILBOX'
    case 'PORTAL_ZELF_UPLOAD':
      return 'PORTAL'
    case 'OPLEIDING_AFRONDEN':
      return 'OPLEIDING'
    default:
      return 'HANDMATIG'
  }
}

// ─── Extractie normaliseren ──────────────────────────────────────────────────

/** Leeg veld (voor handmatig of onleesbaar). */
export function legeExtractieVeld<T>(): CertificaatExtractieVeld<T> {
  return { waarde: null, zekerheid: null }
}

// ─── batch-71 verplichte-certificaten (FO-541 §D9) - typesuggestie bij een upload ──

/** Woorden in een bestandsnaam die niets over het type zeggen. */
const BESTANDSNAAM_RUISWOORDEN = new Set([
  'certificaat', 'certificaten', 'diploma', 'bewijs', 'pasje', 'pas', 'scan', 'scans', 'kopie', 'copy', 'foto',
  'img', 'image', 'dsc', 'document', 'doc', 'def', 'definitief', 'nieuw', 'versie', 'final', 'pdf', 'jpg', 'jpeg', 'png', 'webp', 'heic',
])

/**
 * De bruikbare woorden uit een bestandsnaam: zonder extensie, jaartallen, datums, lange
 * nummers en ruiswoorden. "VCA vol 2024.pdf" wordt "VCA vol"; "IMG_2041.jpg" wordt leeg.
 */
export function schoneCertificaatBestandsnaam(bestandsnaam: string | null | undefined): string {
  const zonderExtensie = (bestandsnaam ?? '').replace(/\.[a-z0-9]{1,5}$/i, '')
  return zonderExtensie
    .split(/[^\p{L}\p{N}]+/u)
    .filter((w) => w.length > 0)
    // Jaartallen, delen van een datum en lange (foto)nummers; "3140" uit NEN 3140 blijft staan.
    .filter((w) => !/^(?:\d{1,2}|(?:19|20)\d{2}|\d{5,})$/.test(w))
    .filter((w) => !BESTANDSNAAM_RUISWOORDEN.has(w.toLowerCase()))
    .join(' ')
}

/**
 * batch-71 verplichte-certificaten (FO-541 §D9, T9) - een kandidaat-synoniem uit de bestandsnaam,
 * voor als de medewerker zelf geen naam gaf ("weet ik niet", of een verkeerd gekozen type).
 *
 * - De opgeschoonde naam zonder de eigen naam van de medewerker, ook niet aaneengeschreven
 *   ("VCA_JJansen.pdf" wordt "VCA").
 * - Alleen als hij herkenbaar bij het gekozen type hoort: een woord gelijk aan, of (vanaf vier
 *   tekens) met daarin, een woord van de typenaam of code. "Heftruckpas" en "IPAF 3B" wel;
 *   "WhatsApp at", "Gescand", "PXL" of "scan0001" niet. Anders onthoudt de catalogus rommel bij het
 *   eerste type en stelt hij dat daarna bij elke upload met zo'n naam voor.
 *
 * Null = niets te onthouden. Een te algemene naam ("VCA") weigert de schrijver
 * (`onthoudCertificaatSynoniem`) zelf.
 */
export function certificaatSynoniemUitBestandsnaam(
  bestandsnaam: string | null | undefined,
  persoonsnamen: ReadonlyArray<string | null | undefined>,
  type: { name: string; code: string | null },
): string | null {
  const persoonsWoorden = [
    ...new Set(
      persoonsnamen
        .join(' ')
        .toLowerCase()
        .split(/[^\p{L}\p{N}]+/u)
        .filter((w) => w.length > 0),
    ),
  ]
  const isPersoon = (woord: string): boolean => {
    const w = woord.toLowerCase()
    return persoonsWoorden.some((p) => p === w || (p.length >= 4 && w.includes(p)))
  }
  const naam = schoneCertificaatBestandsnaam(bestandsnaam)
    .split(' ')
    .filter((w) => w.length > 0 && !isPersoon(w))
    .join(' ')
  if (!/\p{L}{2,}/u.test(naam)) return null
  const typeWoorden = normaliseerCertificaatNaam(`${type.name} ${type.code ?? ''}`)
    .split(' ')
    .filter((t) => t.length >= 2)
  const hoortBijType = normaliseerCertificaatNaam(naam)
    .split(' ')
    .filter((w) => w.length > 0)
    .some((w) => typeWoorden.some((t) => t === w || (t.length >= 4 && w.includes(t)) || (w.length >= 4 && t.includes(w))))
  return hoortBijType ? naam : null
}

export interface CertificaatTypeSuggestie {
  certificateTypeId: string
  /** Hoe het type gevonden is: de hele naam, of doordat al zijn woorden in de tekst staan. */
  via: CertificaatTypeMatchVia | 'WOORDEN'
}

/**
 * Een voorstel voor het type uit een bestandsnaam of de tekst van een document. Alleen een
 * suggestie (de medewerker bevestigt, HR controleert), dus ruimer dan `matchCertificaatType`:
 * ook "alle woorden van een typenaam of synoniem staan in de tekst". Past er meer dan één type
 * even goed, dan is er geen voorstel: een verkeerd voorstel kost meer dan geen voorstel.
 */
export function suggereerCertificaatType(
  tekst: string | null | undefined,
  types: readonly CertificaatTypeVoorMatch[],
  synoniemen: readonly CertificaatSynoniemVoorMatch[],
): CertificaatTypeSuggestie | null {
  const schoon = schoneCertificaatBestandsnaam(tekst)
  const exact = matchCertificaatType(schoon, types, synoniemen)
  if (exact.uitkomst === 'GEVONDEN' && exact.certificateTypeId && exact.via) {
    return { certificateTypeId: exact.certificateTypeId, via: exact.via }
  }
  const woorden = new Set(normaliseerCertificaatNaam(schoon).split(' ').filter((w) => w.length > 0))
  if (woorden.size === 0) return null

  const bruikbaar = types.filter((t) => t.isActive && !isVerzamelCertificaatType(t))
  const bruikbaarIds = new Set(bruikbaar.map((t) => t.id))
  const kandidaten: Array<{ id: string; sleutel: string }> = [
    ...bruikbaar.flatMap((t) => [
      { id: t.id, sleutel: normaliseerCertificaatNaam(t.name) },
      { id: t.id, sleutel: normaliseerCertificaatNaam(t.code) },
      ...naamDelen(t.name).map((d) => ({ id: t.id, sleutel: normaliseerCertificaatNaam(d) })),
    ]),
    ...synoniemen
      .filter((s) => bruikbaarIds.has(s.certificateTypeId))
      .map((s) => ({ id: s.certificateTypeId, sleutel: s.sleutel })),
  ]

  // Per type de langste passende sleutel; de langste over alle types wint (VCA Vol boven VCA).
  const beste = new Map<string, number>()
  for (const k of kandidaten) {
    const delen = k.sleutel.split(' ').filter((w) => w.length > 0)
    if (delen.length === 0 || !delen.every((w) => woorden.has(w))) continue
    beste.set(k.id, Math.max(beste.get(k.id) ?? 0, delen.length))
  }
  if (beste.size === 0) return null
  const hoogste = Math.max(...beste.values())
  const winnaars = [...beste.entries()].filter(([, n]) => n === hoogste)
  if (winnaars.length !== 1) return null
  return { certificateTypeId: winnaars[0]![0], via: 'WOORDEN' }
}
